import React, { useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useAdminAuth } from "../context/AdminAuthContext";
import { ROLE_LABELS, roleHasAccess } from "../utils/adminRoles";

const NAV_ITEMS = [
  { id: "dashboard", label: "Overview", to: "/" },
  { id: "tournaments", label: "Tournaments", to: "/tournaments" },
  { id: "users", label: "Users", to: "/users" },
  { id: "leads", label: "Leads", to: "/leads" },
  { id: "kyc", label: "KYC Approvals", to: "/kyc-approvals" },
  { id: "communities", label: "Communities", to: "/communities" },
  { id: "creators", label: "Creators", to: "/creators" },
  { id: "wallet", label: "Wallet & Payments", to: "/wallet" },
  { id: "reports", label: "Reports", to: "/reports" },
  { id: "settings", label: "Settings", to: "/settings" },
];

const getPageTitle = (pathname) => {
  if (pathname === "/") return "Overview";
  if (pathname.startsWith("/tournaments")) return "Tournaments";
  if (pathname.startsWith("/groups")) return "Groups & Rounds";
  if (pathname.startsWith("/users")) return "Users";
  if (pathname.startsWith("/leads")) return "Leads";
  if (pathname.startsWith("/kyc-approvals")) return "KYC Approvals";
  if (pathname.startsWith("/communities")) return "Communities";
  if (pathname.startsWith("/creators")) return "Creators";
  if (pathname.startsWith("/wallet")) return "Wallet & Payments";
  if (pathname.startsWith("/reports")) return "Reports";
  if (pathname.startsWith("/settings")) return "Settings";
  return "Admin";
};

const AdminLayout = () => {
  const location = useLocation();
  const { user, role, signOut, moduleAccess } = useAdminAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = useMemo(
    () => NAV_ITEMS.filter((item) => roleHasAccess(role, item.id, moduleAccess)),
    [role, moduleAccess]
  );

  const pageTitle = getPageTitle(location.pathname);
  const roleLabel = ROLE_LABELS[role] || "";

  return (
    <div className="admin-shell">
      <aside className={`sidebar ${sidebarOpen ? "open" : ""}`}>
        <div className="sidebar-header">
          <div className="brand">
            <div className="brand-mark">AA</div>
            <div>
              <div className="brand-title">AA Play</div>
              <div className="brand-subtitle">Admin Console</div>
            </div>
          </div>
          <button
            className="sidebar-close"
            onClick={() => setSidebarOpen(false)}
            type="button"
          >
            Close
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.id}
              to={item.to}
              end={item.to === "/"}
              className={({ isActive }) =>
                `nav-item ${isActive ? "active" : ""}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="role-chip">{roleLabel}</div>
          <div className="user-email" title={user?.email || ""}>
            {user?.email || "Signed out"}
          </div>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div className="topbar-left">
            <button
              className="sidebar-toggle"
              type="button"
              onClick={() => setSidebarOpen(true)}
            >
              Menu
            </button>
            <div>
              <div className="topbar-title">{pageTitle}</div>
              <div className="topbar-subtitle">Live admin operations</div>
            </div>
          </div>
          <div className="topbar-actions">
            <div className="role-badge">{roleLabel}</div>
            <button className="button ghost" type="button" onClick={signOut}>
              Sign out
            </button>
          </div>
        </header>
        <div className="content">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
